import type { CompanySubscription, SubscriptionPlan, AvailablePlan } from '@/features/settings/types/settings.types';

// Re-export for convenience
export type { CompanySubscription, SubscriptionPlan, AvailablePlan };

/**
 * Subscription features that can be gated
 */
export type SubscriptionFeature =
  | 'shops'
  | 'products'
  | 'employees'
  | 'ads'
  | 'priceComparison'
  | 'advancedMetrics'
  | 'deleteShops'
  | 'deleteProducts';

/**
 * Plan tiers for easy comparison
 */
export type PlanTier = 'free' | 'medium' | 'ultim' | 'enterprise';

/**
 * Feature access check result
 */
export interface FeatureAccess {
  hasAccess: boolean;
  limit?: number;
  current?: number;
  remaining?: number;
  requiredPlan?: PlanTier;
}

/**
 * Creation capability check result
 */
export interface CreationAccess {
  canCreate: boolean;
  remaining: number;
  limit: number;
  current: number;
}

/**
 * Plan feature limits configuration
 */
export interface PlanLimits {
  maxShops: number;
  maxProducts: number;
  hasMultipleEmployeeAccounts: boolean;
  adsAccess: boolean;
  priceComparison: boolean;
  canDeleteShops: boolean;
  canDeleteProducts: boolean;
  metricsLevel: number;
}

/**
 * Default plan limits by tier
 */
export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    maxShops: 1,
    maxProducts: 20,
    hasMultipleEmployeeAccounts: false,
    adsAccess: false,
    priceComparison: false,
    canDeleteShops: false,
    canDeleteProducts: false,
    metricsLevel: 0,
  },
  medium: {
    maxShops: 3,
    maxProducts: 100,
    hasMultipleEmployeeAccounts: true,
    adsAccess: true,
    priceComparison: false,
    canDeleteShops: false,
    canDeleteProducts: true,
    metricsLevel: 1,
  },
  ultim: {
    maxShops: 10,
    maxProducts: 500,
    hasMultipleEmployeeAccounts: true,
    adsAccess: true,
    priceComparison: true,
    canDeleteShops: true,
    canDeleteProducts: true,
    metricsLevel: 2,
  },
  enterprise: {
    maxShops: 1000,
    maxProducts: 10000,
    hasMultipleEmployeeAccounts: true,
    adsAccess: true,
    priceComparison: true,
    canDeleteShops: true,
    canDeleteProducts: true,
    metricsLevel: 2,
  },
};

/**
 * Feature to required plan mapping
 */
export const FEATURE_REQUIRED_PLAN: Record<SubscriptionFeature, PlanTier> = {
  shops: 'free',
  products: 'free',
  employees: 'medium',
  ads: 'medium',
  priceComparison: 'ultim',
  advancedMetrics: 'medium',
  deleteShops: 'ultim',
  deleteProducts: 'medium',
};

/**
 * Subscription state for the store
 */
export interface SubscriptionState {
  subscription: CompanySubscription | null;
  currentShopsCount: number;
  currentProductsCount: number;
  isLoaded: boolean;
}

/**
 * Subscription store actions
 */
export interface SubscriptionActions {
  setSubscription: (subscription: CompanySubscription | null) => void;
  setCurrentCounts: (shops: number, products: number) => void;
  setLoaded: (loaded: boolean) => void;
  reset: () => void;

  // Feature checks
  canCreateShop: () => boolean;
  canCreateProduct: () => boolean;
  canManageEmployees: () => boolean;
  canAccessAds: () => boolean;
  canAccessPriceComparison: () => boolean;
  canDeleteShop: () => boolean;
  canDeleteProduct: () => boolean;
  hasAdvancedMetrics: () => boolean;

  // Limits
  getShopsLimit: () => number;
  getProductsLimit: () => number;
  getMetricsLevel: () => number;
  getRemainingShops: () => number;
  getRemainingProducts: () => number;

  // Plan info
  getPlanTier: () => PlanTier;
  getPlan: () => SubscriptionPlan | null;
}
